<div class="projects-page">

  <!-- Header -->
  <div class="page-header">
    <div class="header-left">
      <h1 class="page-title">
        <mat-icon>folder_open</mat-icon>
        Projects
      </h1>
      <span class="project-count" *ngIf="!loading()">{{ totalCount() }} project{{ totalCount() !== 1 ? 's' : '' }}</span>
    </div>
    <div class="header-actions">
      <button mat-raised-button color="primary" (click)="createNewProject()">
        <mat-icon>add</mat-icon>
        New Project
      </button>
    </div>
  </div>

  <!-- Filters -->
  <div class="filter-bar tt-sticky-toolbar">
    <mat-form-field appearance="outline" class="search-field">
      <mat-label>Search projects…</mat-label>
      <mat-icon matPrefix>search</mat-icon>
      <input matInput [value]="searchQuery" (input)="onSearch($any($event.target).value)" placeholder="Name or code" />
      <button mat-icon-button matSuffix *ngIf="searchQuery" (click)="onSearch('')">
        <mat-icon>close</mat-icon>
      </button>
    </mat-form-field>

    <mat-form-field appearance="outline" class="status-filter">
      <mat-label>Category</mat-label>
      <mat-select [(ngModel)]="categoryFilter" (ngModelChange)="onCategoryChange()">
        <mat-option value="">All Categories</mat-option>
        <mat-option *ngFor="let c of categoryOptions" [value]="c">{{ c }}</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="status-filter">
      <mat-label>Status</mat-label>
      <mat-select [(ngModel)]="statusFilter" (ngModelChange)="onStatusChange()">
        <mat-option value="">All Statuses</mat-option>
        <mat-option *ngFor="let s of statusOptions" [value]="s.value">{{ s.label }}</mat-option>
      </mat-select>
    </mat-form-field>

    <button mat-stroked-button
      [class.active-filter]="favoritesOnlyFilter"
      (click)="favoritesOnlyFilter = !favoritesOnlyFilter; onFavoritesOnlyChange()">
      <mat-icon>{{ favoritesOnlyFilter ? 'star' : 'star_border' }}</mat-icon>
      Favorites
    </button>

    <button mat-stroked-button
      [class.active-filter]="templatesOnlyFilter"
      (click)="templatesOnlyFilter = !templatesOnlyFilter; onTemplatesOnlyChange()">
      <mat-icon>{{ templatesOnlyFilter ? 'dashboard_customize' : 'dashboard' }}</mat-icon>
      Templates
    </button>

    <button mat-stroked-button
      [class.active-filter]="activeOnlyFilter"
      (click)="activeOnlyFilter = !activeOnlyFilter; onActiveOnlyChange()">
      <mat-icon>{{ activeOnlyFilter ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
      Active Only
    </button>

    <button mat-icon-button matTooltip="Clear filters"
      *ngIf="searchQuery || statusFilter !== '' || categoryFilter || activeOnlyFilter || favoritesOnlyFilter || templatesOnlyFilter"
      (click)="clearFilters()">
      <mat-icon>filter_alt_off</mat-icon>
    </button>
  </div>

  <!-- Error -->
  <div class="error-banner" *ngIf="error()">
    <mat-icon>error_outline</mat-icon>
    {{ error() }}
    <button mat-icon-button (click)="error.set(null)"><mat-icon>close</mat-icon></button>
  </div>

  <!-- Loading -->
  @if (loading()) {
    <app-skeleton variant="card" [count]="6"></app-skeleton>
  }

  <!-- Empty State -->
  @if (!loading() && projects().length === 0) {
    <div class="tt-empty-state">
      <mat-icon>folder_off</mat-icon>
      <h3>No projects found</h3>
      @if (searchQuery || statusFilter !== '' || activeOnlyFilter) {
        <p>
          Try adjusting your filters or
          <button mat-button type="button" class="link-btn" (click)="clearFilters()">clear all filters</button>.
        </p>
      } @else {
        <p>Get started by creating your first project.</p>
        <div class="tt-empty-state__actions">
          <button mat-raised-button color="primary" (click)="createNewProject()">
            <mat-icon>add</mat-icon>
            Create Project
          </button>
        </div>
      }
    </div>
  }

  <!-- Project Grid -->
  @if (!loading() && projects().length > 0) {
    <div class="project-grid">
      @for (project of projects(); track trackById($index, project)) {
    <mat-card
      class="project-card tt-compact-card"
      (click)="openProject(project)">

      <!-- Color Bar -->
      <div class="color-bar" [style.background-color]="project.color || '#6366f1'"></div>

      <mat-card-header>
        <div class="card-header-inner">
          <div class="project-title-row">
            <button mat-icon-button class="favorite-btn" (click)="toggleFavorite(project, $event)"
              [matTooltip]="project.isFavorite ? 'Remove favorite' : 'Add favorite'">
              <mat-icon [class.favorited]="project.isFavorite">{{ project.isFavorite ? 'star' : 'star_border' }}</mat-icon>
            </button>
            <h3 class="project-name" [matTooltip]="project.name">{{ project.name }}</h3>
            <span class="project-code" *ngIf="project.code">{{ project.code }}</span>
          </div>
          <button mat-icon-button class="more-btn" [matMenuTriggerFor]="projectMenu" (click)="$event.stopPropagation()">
            <mat-icon>more_vert</mat-icon>
          </button>
          <mat-menu #projectMenu="matMenu">
            <button mat-menu-item (click)="openProject(project)">
              <mat-icon>open_in_new</mat-icon> Open
            </button>
            <button mat-menu-item (click)="addTask(project, $event)">
              <mat-icon>add_task</mat-icon> Add Task
            </button>
            <button mat-menu-item (click)="editProject(project, $event)">
              <mat-icon>edit</mat-icon> Edit
            </button>
            <button mat-menu-item (click)="duplicateProject(project, $event)">
              <mat-icon>content_copy</mat-icon> Duplicate
            </button>
            <mat-divider></mat-divider>
            <button mat-menu-item class="danger-item" (click)="deleteProject(project, $event)">
              <mat-icon>delete</mat-icon> Delete
            </button>
          </mat-menu>
        </div>
      </mat-card-header>

      <mat-card-content>
        <p class="project-description" *ngIf="project.description">{{ project.description }}</p>

        <div class="badge-row">
          <span class="status-badge" [ngClass]="statusColorMap[project.status]">
            {{ statusLabels[project.status] || project.status }}
          </span>
          <span class="archived-badge" *ngIf="project.archivedAt">Archived</span>
          <span class="template-badge" *ngIf="project.isTemplate">Template</span>
          <span class="category-badge" *ngIf="project.category">{{ project.category }}</span>
          <span class="priority-badge" [ngClass]="priorityColorMap[project.priority]">
            {{ priorityLabels[project.priority] || project.priority }}
          </span>
          <span class="published-badge" *ngIf="project.isPublishedApp">
            <mat-icon>publish</mat-icon>
            {{ project.platform || 'App' }}
          </span>
        </div>

        <!-- Progress -->
        <div class="progress-section">
          <div class="progress-header">
            <span class="progress-label">Progress</span>
            <span class="progress-value">{{ project.progress | number:'1.0-0' }}%</span>
          </div>
          <div class="progress-bar">
            <div class="progress-fill" [ngClass]="getProgressColor(project.progress)"
              [style.width.%]="project.progress"></div>
          </div>
        </div>

        <!-- Stats Row -->
        <div class="stats-row">
          <div class="stat">
            <mat-icon>group</mat-icon>
            <span>{{ project.memberCount }}</span>
          </div>
          <div class="stat">
            <mat-icon>task_alt</mat-icon>
            <span>{{ project.taskCount - project.openTaskCount }}/{{ project.taskCount }}</span>
          </div>
          <div class="stat" *ngIf="project.accountName">
            <mat-icon>business</mat-icon>
            <span class="truncate">{{ project.accountName }}</span>
          </div>
          <div class="stat" *ngIf="project.dueDate">
            <mat-icon>event</mat-icon>
            <span>{{ project.dueDate | date:'mediumDate' }}</span>
          </div>
        </div>

        <div class="card-actions" (click)="$event.stopPropagation()">
          <button mat-stroked-button color="primary" type="button" (click)="addTask(project, $event)">
            <mat-icon>add_task</mat-icon>
            Add Task
          </button>
        </div>
      </mat-card-content>
    </mat-card>
      }
    </div>
  }

  <!-- Pagination -->
  @if (!loading() && totalCount() > pageSize) {
  <mat-paginator
    [length]="totalCount()"
    [pageSize]="pageSize"
    [pageIndex]="page"
    [pageSizeOptions]="[6, 12, 24, 48]"
    (page)="onPageChange($event)"
    showFirstLastButtons>
  </mat-paginator>
  }

</div>
